import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { observer } from 'mobx-react-lite';
import { Room } from 'livekit-client';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@web-chat-core/ui-kit';
import { RaiseHandButton } from 'features/RaiseHand/components/RaiseHandButton';
import { RecordPanel } from 'features/Recording';
import { ScreenShareButton } from 'features/ScreenShare/containers/ScreenShareButton';
import { ReactionsButton } from 'features/Reactions';
import { AudioButton, VideoButton } from 'ui/components';
import { useParticipant } from 'core';
import { ReactComponent as IconEndCall } from 'static/ic-end-call.svg';
import { useUserSettingsContext } from 'contexts';
import { isFeatureOn } from 'useCase/featureFlags/featureFlagsApi';
import { FEATURES } from 'consts';
import { isCameraHardLocked, isMicHardLocked, isScreenShareHardLocked } from 'useCase/webinar/webinarStatus';
import { AdditionalMenu } from '../AdditionalMenu/AdditionalMenu';
import { ControlsContainer, HiddenOnMobilePanel, LeaveRoomButton, LockedControlWrapper } from './view';

export interface ControlsProps {
    room: Room;
    enableScreenShare?: boolean;
    lowParams?: boolean;
    goodParams?: boolean;
    receiveLowStream?: boolean;
    sendLowStream?: boolean;
    enableAudio?: boolean;
    enableVideo?: boolean;
    onLeave?: (room: Room) => void;
    isInterfaceHidden?: boolean;
    registerAlwaysVisibleElement?: (ref: RefObject<HTMLElement | null>) => () => void;
}

const ControlsPanelInner = ({
    room,
    onLeave,
    enableScreenShare = true,
    isInterfaceHidden,
    registerAlwaysVisibleElement,
}: ControlsProps) => {
    const { t } = useTranslation();
    const reactionsButtonRef = useRef<HTMLDivElement | null>(null);
    const controlsContainerRef = useRef<HTMLDivElement | null>(null);
    const {
        cameraPublication: camPub,
        microphonePublication: micPub,
        isSpeaking,
    } = useParticipant(room.localParticipant);
    const {
        audioEnabled,
        setAudioEnabled,
        videoEnabled,
        setVideoEnabled,
        setAudioInputDeviceId,
        setAudioOutputDeviceId,
        setVideoDeviceId,
    } = useUserSettingsContext();

    const isReactionsEnabled = isFeatureOn(FEATURES.VCS_REACTIONS_ENABLED);

    const localParticipant = room.localParticipant;
    const isMicLocked = isMicHardLocked(localParticipant);
    const isCamLocked = isCameraHardLocked(localParticipant);
    const isScreenShareLocked = isScreenShareHardLocked(localParticipant);

    const isAudioStreamEnabled = micPub && !micPub.isMuted;
    const isVideoStreamEnabled = camPub?.track && !camPub.track.isMuted;

    const [audioButtonDisabled, setAudioButtonDisabled] = useState(false);
    const [videoButtonDisabled, setVideoButtonDisabled] = useState(false);

    // This useEffect fixed bug FB-8062.
    // Temporary fix, the problem is in the LiveKit core.
    // Please, do not modify or remove this useEffect.
    useEffect(() => {
        if (videoButtonDisabled || audioButtonDisabled) return;

        if (isCamLocked) {
            if (videoEnabled) {
                setVideoEnabled(false);
            }
            if (isVideoStreamEnabled) {
                room.localParticipant.setCameraEnabled(false);
            }
        } else if (videoEnabled !== isVideoStreamEnabled) {
            room.localParticipant.setCameraEnabled(videoEnabled);
        }

        if (isMicLocked) {
            if (audioEnabled) {
                setAudioEnabled(false);
            }
            if (isAudioStreamEnabled) {
                room.localParticipant.setMicrophoneEnabled(false);
            }
        } else if (audioEnabled !== isAudioStreamEnabled) {
            room.localParticipant.setMicrophoneEnabled(audioEnabled);
        }
    }, [
        audioEnabled,
        videoEnabled,
        room.localParticipant,
        isAudioStreamEnabled,
        isVideoStreamEnabled,
        isMicLocked,
        isCamLocked,
        videoButtonDisabled,
        audioButtonDisabled,
        setAudioEnabled,
        setVideoEnabled,
    ]);

    const handleAudioToggle = useCallback(async () => {
        const nextAudioEnabled = !audioEnabled;
        setAudioEnabled(nextAudioEnabled);
        setAudioButtonDisabled(true);
        room.localParticipant
            .setMicrophoneEnabled(nextAudioEnabled)
            .catch(() => {
                setAudioEnabled(!nextAudioEnabled);
            })
            .finally(() => {
                setAudioButtonDisabled(false);
            });
    }, [audioEnabled, room.localParticipant, setAudioEnabled]);

    const handleVideoToggle = useCallback(() => {
        const nextVideoEnabled = !videoEnabled;
        setVideoEnabled(nextVideoEnabled);
        setVideoButtonDisabled(true);
        room.localParticipant
            .setCameraEnabled(nextVideoEnabled)
            .catch(() => {
                setVideoEnabled(!nextVideoEnabled);
            })
            .finally(() => {
                setVideoButtonDisabled(false);
            });
    }, [videoEnabled, room.localParticipant, setVideoEnabled]);

    const handleInputAudioSourceSelected = useCallback(
        (device: { deviceId: string }) => {
            setAudioButtonDisabled(true);
            room.switchActiveDevice('audioinput', device.deviceId)
                .then(() => {
                    setAudioInputDeviceId(device.deviceId);
                })
                .catch((e) => {
                    console.warn('Error switching audioinput device', e);
                })
                .finally(() => {
                    setAudioButtonDisabled(false);
                });
        },
        [room, setAudioInputDeviceId],
    );

    const handleOutputAudioSourceSelected = useCallback(
        (device: { deviceId: string }) => {
            setAudioButtonDisabled(true);
            room.switchActiveDevice('audiooutput', device.deviceId)
                .then(() => {
                    setAudioOutputDeviceId(device.deviceId);
                })
                .catch((e) => {
                    console.warn('Error switching audiooutput device', e);
                })
                .finally(() => {
                    setAudioButtonDisabled(false);
                });
        },
        [room, setAudioOutputDeviceId],
    );

    const handleVideoSourceSelected = useCallback(
        (device: { deviceId: string }) => {
            setVideoButtonDisabled(true);
            room.switchActiveDevice('videoinput', device.deviceId)
                .then(() => {
                    setVideoDeviceId(device.deviceId);
                })
                .catch((e) => {
                    console.warn('Error switching videoinput device', e);
                })
                .finally(() => {
                    setVideoButtonDisabled(false);
                });
        },
        [room, setVideoDeviceId],
    );

    const handleLeave = useCallback(() => {
        room.disconnect();
        onLeave?.(room);
    }, [room, onLeave]);

    const isPushToTalkEnabled = isFeatureOn(FEATURES.VCS_MIC_PUSH_ENABLED);
    const isMicToggleEnabled = isFeatureOn(FEATURES.VCS_MIC_TOGGLE_ENABLED);
    const isAdvancedSoundAndCameraSettingsEnabled = isFeatureOn(FEATURES.VCS_ADVANCED_SOUND_CAMERA_SETTINGS_ENABLED);
    const isSpeakerAnimationsEnabled = isFeatureOn(FEATURES.VCS_SPEAKER_ANIMATIONS_ENABLED);

    return (
        <ControlsContainer ref={controlsContainerRef}>
            {isMicLocked ? (
                <Tooltip label={t('vks_webinar_mic_restricted_tooltip')} sideOffset={8}>
                    <LockedControlWrapper>
                        {/* Кнопка заблокирована (pointer-events: none), поэтому хендлеры и пропсы меню не нужны */}
                        <AudioButton />
                    </LockedControlWrapper>
                </Tooltip>
            ) : (
                <AudioButton
                    isButtonDisabled={audioButtonDisabled}
                    isPushToTalkEnabled={isPushToTalkEnabled}
                    isToggleMicEnabled={isMicToggleEnabled}
                    isAdvancedSoundAndCameraSettingsEnabled={isAdvancedSoundAndCameraSettingsEnabled}
                    isSpeakerAnimationsEnabled={isSpeaking && isSpeakerAnimationsEnabled}
                    onClick={handleAudioToggle}
                    onAudioInputSourceSelected={handleInputAudioSourceSelected}
                    onAudioOutputSourceSelected={handleOutputAudioSourceSelected}
                />
            )}
            {isCamLocked ? (
                <Tooltip label={t('vks_webinar_camera_restricted_tooltip')} sideOffset={8}>
                    <LockedControlWrapper>
                        {/* Кнопка заблокирована (pointer-events: none), поэтому хендлеры и пропсы меню не нужны */}
                        <VideoButton />
                    </LockedControlWrapper>
                </Tooltip>
            ) : (
                <VideoButton
                    isButtonDisabled={videoButtonDisabled}
                    isAdvancedSoundAndCameraSettingsEnabled={isAdvancedSoundAndCameraSettingsEnabled}
                    onClick={handleVideoToggle}
                    onSourceSelected={handleVideoSourceSelected}
                />
            )}

            {isReactionsEnabled && !isInterfaceHidden && (
                <ReactionsButton
                    buttonRef={reactionsButtonRef}
                    controlsContainerRef={controlsContainerRef}
                    registerAlwaysVisibleElement={registerAlwaysVisibleElement}
                />
            )}
            {enableScreenShare &&
                (isScreenShareLocked ? (
                    <Tooltip label={t('vks_webinar_screen_share_restricted_tooltip')} sideOffset={8}>
                        <LockedControlWrapper>
                            <ScreenShareButton />
                        </LockedControlWrapper>
                    </Tooltip>
                ) : (
                    <ScreenShareButton />
                ))}
            <HiddenOnMobilePanel>
                <RaiseHandButton participant={room.localParticipant} />
            </HiddenOnMobilePanel>
            <HiddenOnMobilePanel>
                <RecordPanel />
            </HiddenOnMobilePanel>
            <AdditionalMenu room={room} />
            {onLeave && (
                <LeaveRoomButton
                    IconImage={IconEndCall}
                    title={t('web_call_exit')}
                    onClick={handleLeave}
                    variant="danger"
                    shape="circle"
                />
            )}
        </ControlsContainer>
    );
};

export const ControlsPanel = observer(ControlsPanelInner);
